import { z } from 'zod'
import { apiService } from './api'
import { type Crisis, type CreateCrisisData, type UpdateCrisisData } from '../types'
import { CRISIS_ENDPOINTS } from '../../api/endpoints'

// Response schemas
const CrisisSchema = z.object({
  id: z.string(),
  type: z.string(),
  location: z.string(),
  description: z.string(),
  status: z.enum(['active', 'closed']),
  createdAt: z.string(),
  closedAt: z.string().nullable().optional(),
  duration: z.number().nullable().optional(),
  practiceCaseId: z.string().nullable().optional(),
  createdBy: z.object({
    id: z.string(),
    name: z.string(),
  }),
})

const CrisisListSchema = z.array(CrisisSchema)

const CreateCrisisResponseSchema = z.object({
  data: CrisisSchema,
})

const CrisisListResponseSchema = z.object({
  data: CrisisListSchema,
})

export class CrisisService {
  async createCrisis(data: CreateCrisisData): Promise<Crisis> {
    const response = await apiService.post(
      CRISIS_ENDPOINTS.LIST,
      data,
      undefined,
      CreateCrisisResponseSchema
    )
    return response.data
  }

  async getActiveCrisis(): Promise<Crisis | null> {
    try {
      const response = await apiService.get(
        CRISIS_ENDPOINTS.ACTIVE,
        z.object({ data: CrisisSchema.nullable() })
      )
      return response.data
    } catch (error) {
      // If no active crisis, return null
      if (error instanceof Error && error.message.includes('not_found')) {
        return null
      }
      throw error
    }
  }

  async getCrisis(crisisId: string): Promise<Crisis> {
    const response = await apiService.get(
      CRISIS_ENDPOINTS.CRISIS_BY_ID(crisisId),
      z.object({ data: CrisisSchema })
    )
    return response.data
  }

  async getCrises(includeClosed = true): Promise<Crisis[]> {
    const params = includeClosed ? {} : { include_closed: 'false' }
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${CRISIS_ENDPOINTS.LIST}?${queryString}` : CRISIS_ENDPOINTS.LIST

    const response = await apiService.get(url, CrisisListResponseSchema)
    return response.data
  }

  async updateCrisis(crisisId: string, data: UpdateCrisisData): Promise<Crisis> {
    const response = await apiService.put(
      CRISIS_ENDPOINTS.CRISIS_BY_ID(crisisId),
      data,
      z.object({ data: CrisisSchema })
    )
    return response.data
  }

  async closeCrisis(crisisId: string): Promise<void> {
    await apiService.delete(CRISIS_ENDPOINTS.CRISIS_BY_ID(crisisId))
  }

  // Helper method to select/activate a crisis (for historical viewing)
  async selectCrisis(crisisId: string): Promise<Crisis> {
    // For now, just get the crisis - in the future this might activate it differently
    return this.getCrisis(crisisId)
  }
}

export const crisisService = new CrisisService()
